import * as THREE from 'three';

/**
 * Utility to check if a model has lights
 * @param {Object} scene - The GLTF scene object
 * @returns {Boolean} - Whether the model contains lights
 */
export const modelHasLights = (scene) => {
  let hasLights = false;
  scene.traverse((object) => {
    if (object.isLight) {
      hasLights = true;
    }
  });
  return hasLights;
};

/**
 * Base processor class for model transformations
 */
export class ModelProcessor {
  process(scene) {
    // Base implementation does nothing
    return scene;
  }
}

/**
 * Processor to handle lights in imported models
 */
export class LightProcessor extends ModelProcessor {
  constructor(options = {}) {
    super();
    // Default options
    this.options = {
      // Whether to disable all lights in the model
      disableLights: true,
      // Intensity multiplier for lights (used if not disabling)
      intensityFactor: 0.3,
      // Maximum allowed light intensity
      maxIntensity: 0.5,
      ...options
    };
  }

  process(scene) {
    if (!scene) return scene;
    
    try {
      scene.traverse((object) => {
        if (object.isLight) {
          console.log('Processing model light:', object.type);
          
          if (this.options.disableLights) {
            // Option 1: Make light invisible but keep it
            object.visible = false;
          } else {
            // Option 2: Reduce intensity
            const currentIntensity = object.intensity || 1;
            object.intensity = Math.min(
              currentIntensity * this.options.intensityFactor,
              this.options.maxIntensity
            );
          }
        }
      });
    } catch (error) {
      console.error('Error processing model lights:', error);
    }
    
    return scene;
  }
}

/**
 * Pipeline manager to apply a series of processors to a model
 */
export class ModelProcessingPipeline {
  constructor(processors = []) {
    this.processors = processors;
  }

  addProcessor(processor) {
    this.processors.push(processor);
    return this;
  }

  process(scene, options = {}) {
    if (!scene) return scene;
    
    // Clone the scene if requested to avoid modifying the original
    const targetScene = options.clone ? scene.clone() : scene;
    
    try {
      return this.processors.reduce(
        (processedScene, processor) => processor.process(processedScene),
        targetScene
      );
    } catch (error) {
      console.error('Error in model processing pipeline:', error);
      return targetScene;
    }
  }
}

/**
 * Create a default pipeline with common processors
 */
export const createDefaultPipeline = (options = {}) => {
  const pipeline = new ModelProcessingPipeline();
  
  if (options.processLights !== false) {
    pipeline.addProcessor(new LightProcessor(options.lightOptions));
  }
  
  // Add other default processors here as they're implemented
  
  return pipeline;
}; 